import { Flex, Text, HelpIndicator, AgGrid } from "@jsq/ds/core";
import { Icon, IconType } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { BaseStatusTooltip } from "../BaseStatusTooltip";
import { ActionsMenu } from "./ActionsMenu";
import { STATUS_CONFIG } from "./config";
import {
  contentWrapper,
  statusLabelAnchor,
  cellContainer,
  launchIconContainer,
  launchIcon,
  baseContainer,
} from "./styles_vanilla.css";
import { RefreshStatusCellRendererProps, REFRESH_STATUSES_WITH_CASE_LINK } from "./types";

export const RefreshStatusCellRenderer = ({
  value,
  data,
  api,
}: RefreshStatusCellRendererProps & { api?: AgGrid.GridApi }) => {
  if (data?.isEntityClientOnboardingProfile) {
    return (
      <Flex className={baseContainer} alignItems="center">
        <Text>--</Text>
      </Flex>
    );
  }

  if (!value || !STATUS_CONFIG[value]) {
    return (
      <Flex className={baseContainer} alignItems="center">
        <Text>--</Text>
      </Flex>
    );
  }

  const config = STATUS_CONFIG[value];

  const textContent =
    typeof config.text.content === "object"
      ? config.text.content.render(data)
      : config.text.content;

  const shouldShowLaunchIcon =
    data?.refreshCaseId && REFRESH_STATUSES_WITH_CASE_LINK.includes(value);

  const handleLaunchIconClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (data?.refreshCaseId) {
      window.open(`/canopy/compliance/case/${data.refreshCaseId}`, "_blank");
    }
  };

  const iconAndText = (
    <span className={statusLabelAnchor}>
      {config.icon && (
        <Icon
          type={config.icon.type as IconType}
          variant={config.icon.variant}
          className={config.icon.className}
          data-testid={`refresh-status-${value}-icon`}
        />
      )}
      <Text className={config.text.className}>{textContent}</Text>
    </span>
  );

  const tooltipContent = BaseStatusTooltip({
    statusType: value,
    tooltipData: data?.tooltipData,
  });

  return (
    <Flex className={config.containerClass} alignItems="center">
      <Flex className={cellContainer} alignItems="center">
        <div className={contentWrapper}>
          {tooltipContent ? (
            <HelpIndicator
              id={`refresh-status-${data?.id ?? value}-tooltip`}
              placement="top"
              content={tooltipContent}
              fullWidth
            >
              {iconAndText}
            </HelpIndicator>
          ) : (
            iconAndText
          )}
        </div>
        {shouldShowLaunchIcon && (
          <Flex alignItems="center" className={launchIconContainer}>
            <HelpIndicator
              id={`refresh-case-launch-${data.id}-tooltip`}
              placement="top"
              content="View profile refresh case"
            >
              <Icon
                type="external-link"
                variant="outline"
                color={Colors.Primary600}
                onClick={handleLaunchIconClick}
                className={launchIcon}
                data-testid="refresh-case-launch-icon"
              />
            </HelpIndicator>
          </Flex>
        )}
      </Flex>
      <ActionsMenu status={value} data={data} api={api} />
    </Flex>
  );
};
